import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

const HoverTooltip = ({ children, text }) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, vertical: 'top', horizontal: 'center' });
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);

    const calculatePosition = () => {
        if (!containerRef.current || !tooltipRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        let top, left;
        let vertical = 'top';
        let horizontal = 'center';

        // Vertical positioning
        if (rect.top - tooltipRect.height - 10 > 0) { // Enough space on top
            top = rect.top - tooltipRect.height - 10;
            vertical = 'top';
        } else { // Not enough space on top, position below
            top = rect.bottom + 10;
            vertical = 'bottom';
        }

        // Horizontal positioning
        if (rect.left + rect.width / 2 < tooltipRect.width / 2) { // Too close to left edge
            left = rect.left;
            horizontal = 'left';
        } else if (windowWidth - (rect.left + rect.width / 2) < tooltipRect.width / 2) { // Too close to right edge
            left = rect.right - tooltipRect.width;
            horizontal = 'right';
        } else { // Center
            left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
            horizontal = 'center';
        }
        
        setPosition({ top, left, vertical, horizontal });
    };

    const handleMouseEnter = () => {
        if ('ontouchstart' in window) return;
        setShow(true);
    };

    const handleTouchStart = (e) => {
        e.stopPropagation();
        setShow(prev => !prev);
    };

    useEffect(() => {
        if (show) {
            // Position is calculated after the tooltip is rendered and we have its dimensions
            calculatePosition();
        }
    }, [show]);


    useEffect(() => {
        if (!show) return;

        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShow(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [show]);

    const TooltipContent = () => {
        const horizontalAlignmentClasses = {
            center: 'left-1/2 -translate-x-1/2',
            left: 'left-0',
            right: 'right-0',
        };
    
        const arrowAlignmentClasses = {
            center: 'left-1/2 -translate-x-1/2',
            left: 'left-4',
            right: 'right-4',
        }
    
        const tooltipClasses = `tooltip-box fixed z-50`;
        
        const arrowClasses = `absolute w-0 h-0 border-solid border-transparent ${
            position.vertical === 'top' 
                ? 'border-t-gray-800 top-full' 
                : 'border-b-gray-800 bottom-full rotate-180'
        } ${arrowAlignmentClasses[position.horizontal]}`;

        const arrowStyle = {
            borderWidth: '5px',
        };

        return (
            <div 
                ref={tooltipRef} 
                className={tooltipClasses} 
                style={{ 
                    top: `${position.top}px`, 
                    left: `${position.left}px`,
                }}
            >
                {text}
                <div className={arrowClasses} style={arrowStyle} />
            </div>
        );
    };

    return (
        <div 
            ref={containerRef}
            className="relative flex items-center" 
            onMouseEnter={handleMouseEnter} 
            onMouseLeave={() => setShow(false)}
            onTouchStart={handleTouchStart}
        >
            {children}
            {show && text && ReactDOM.createPortal(
                <TooltipContent />,
                document.body
            )}
        </div>
    );
};

export default HoverTooltip;
